import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, KeyRound, Lock, ArrowLeft, CheckCircle, Users, Eye, EyeOff, RotateCcw } from 'lucide-react';
import authAxios from '../../../lib/authAxios';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password, 4: Success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  
  // State for forms
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [resetRequestId, setResetRequestId] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleRequestOtp = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (cooldown > 0) return;

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setError('Please enter your email address');
      return;
    }
    if (/\p{Extended_Pictographic}/u.test(cleanEmail)) {
      setError('Emojis are not allowed in email');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await authAxios.post(`/password-reset/request`, { email: cleanEmail });
      if (response.data.success && response.data.data?.resetRequestId) {
        setResetRequestId(response.data.data.resetRequestId);
        setCooldown(5); // 5-second cooldown before requesting again
        setStep(2);
      } else {
        // Fallback for security (don't reveal if email exists, but we need ID to proceed)
        setError('If your email is registered, you will receive an OTP shortly. (Check your inbox or spam folder)');
        setCooldown(5);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to request password reset';
      setError(msg);
      // If server returned "Please wait X seconds", parse X into cooldown
      const match = msg.match(/wait (\d+) seconds/i);
      if (match && match[1]) {
        setCooldown(parseInt(match[1], 10));
      } else if (err.response?.status === 429) {
        setCooldown(5);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError('OTP must be exactly 6 digits');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await authAxios.post(`/password-reset/verify`, { 
        resetRequestId, 
        otp 
      });
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (/\p{Extended_Pictographic}/u.test(newPassword)) {
      setError('Password cannot contain emojis');
      return;
    }
    
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,72}$/;
    if (!passwordRegex.test(newPassword)) {
      setError('Password must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await authAxios.post(`/password-reset/confirm`, { 
        resetRequestId, 
        newPassword 
      });
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full bg-gray-50">
      {/* Left Side - Branding */}
      <div className="hidden md:flex flex-col justify-between md:w-[35%] lg:w-[45%] bg-accent-indigo p-10 lg:p-16 text-white relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center space-x-3 mb-8">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <Users size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Vet Collab</h1>
              <p className="text-sm text-indigo-100 opacity-80 font-medium">powered by furcare</p>
            </div>
          </div>
          
          <div className="max-w-md mt-12">
            <p className="text-xl leading-relaxed font-light text-indigo-50">
              Connect, collaborate, and advance veterinary medicine together. 
              Join a community of veterinary professionals and students worldwide.
            </p>
          </div>
        </div>

        <div className="relative z-10 text-sm opacity-60">
          © 2026 Tutelage Private Limited. All rights reserved.
        </div>

        {/* Subtle background decoration */}
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-96 h-96 bg-black/10 rounded-full blur-3xl"></div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative">
        <div className="md:hidden absolute top-8 left-8 right-8 flex items-center justify-between">
          <div className="flex items-center space-x-2 text-accent-indigo">
            <Users size={28} />
            <span className="font-bold text-2xl tracking-tight">Vet Collab</span>
          </div>
        </div>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-6">
          <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center">
            {step === 1 && <Mail size={24} />}
            {step === 2 && <KeyRound size={24} />}
            {step === 3 && <Lock size={24} />}
            {step === 4 && <CheckCircle size={24} />}
          </div>
        </div>
        
        <h2 className="text-center text-3xl font-extrabold text-gray-900">
          {step === 1 && "Forgot Password"}
          {step === 2 && "Enter OTP"}
          {step === 3 && "Set New Password"}
          {step === 4 && "Password Reset"}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {step === 1 && "Enter your email address and we'll send you a verification code to reset your password."}
          {step === 2 && `We've sent a 6-digit verification code to ${email}.`}
          {step === 3 && "Your new password must be at least 8 characters long and contain uppercase, lowercase, numbers, and symbols."}
          {step === 4 && "Your password has been successfully reset. You can now log in with your new credentials."}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 border border-gray-100">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {step === 1 && (
            <form onSubmit={handleRequestOtp} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address</label>
                <div className="mt-1">
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="you@example.com"
                  />
                </div>
              </div>
              
              <div>
                <button
                  type="submit"
                  disabled={loading || cooldown > 0}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : cooldown > 0 ? `Wait ${cooldown}s before retrying` : 'Send Verification Code'}
                </button>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleVerifyOtp} className="space-y-6">
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700">Verification Code</label>
                <div className="mt-1 text-center">
                  <input
                    id="otp"
                    type="text"
                    required
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                    className="appearance-none block w-full text-center tracking-widest text-2xl px-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="000000"
                  />
                </div>
              </div>
              
              <div>
                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>
              </div>

              <div className="flex items-center justify-center text-sm pt-1 border-t border-gray-100">
                <button
                  type="button"
                  disabled={loading || cooldown > 0}
                  onClick={handleRequestOtp}
                  className="text-indigo-600 hover:text-indigo-500 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1.5 transition-colors"
                >
                  <RotateCcw size={14} className={loading ? "animate-spin" : ""} />
                  <span>{cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend Code"}</span>
                </button>
              </div>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">New Password</label>
                <div className="mt-1">
                  <div className="relative">
                    <input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>
              
              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">Confirm Password</label>
                <div className="mt-1">
                  <div className="relative">
                    <input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                    >
                      {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>
              
              <div>
                <button
                  type="submit"
                  disabled={loading || !newPassword || !confirmPassword}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <div>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Return to Login
                </button>
              </div>
            </div>
          )}

          {step !== 4 && (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or</span>
                </div>
              </div>

              <div className="mt-6 flex justify-center">
                <Link to="/login" className="flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500 transition-colors">
                  <ArrowLeft size={16} className="mr-1" /> Back to login
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
